import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createLatestRequest, type RequestState } from './latest-request';

export function useScopedRequest<T>(
  scope: string | null,
  load: (signal: AbortSignal) => Promise<T>
) {
  const [state, setState] = useState<RequestState<T>>({
    scope: null,
    data: null,
    loading: false,
    error: null,
  });
  const request = useMemo(() => createLatestRequest<T>(setState), []);
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const reload = useCallback(
    (work = load) =>
      scope && activeScope.current === scope
        ? request.run(scope, work)
        : Promise.resolve(),
    [request, scope, load]
  );
  useEffect(() => {
    activeScope.current = scope;
    if (scope) void request.run(scope, load);
    return () => {
      activeScope.current = null;
      request.cancel();
    };
  }, [scope, load, request]);
  // Hide the previous exporter's data even before the effect has run.
  const visible =
    state.scope === scope && scope !== null
      ? state
      : {
          scope,
          data: null,
          loading: scope !== null,
          error: null,
        };
  return { ...visible, reload };
}
